/**
 * Timezone utility functions for converting between IST and local timezones
 * All slots are stored in IST (Asia/Kolkata) on the backend
 */

// Common timezone options for the dropdown
export const TIMEZONE_OPTIONS = [
  { value: 'Asia/Kolkata', label: 'IST (India Standard Time)', offset: '+05:30' },
  { value: 'America/New_York', label: 'EST/EDT (Eastern Time)', offset: '-05:00/-04:00' },
  { value: 'America/Los_Angeles', label: 'PST/PDT (Pacific Time)', offset: '-08:00/-07:00' },
  { value: 'America/Chicago', label: 'CST/CDT (Central Time)', offset: '-06:00/-05:00' },
  { value: 'Europe/London', label: 'GMT/BST (UK)', offset: '+00:00/+01:00' },
  { value: 'Europe/Paris', label: 'CET/CEST (Central Europe)', offset: '+01:00/+02:00' },
  { value: 'Europe/Berlin', label: 'CET/CEST (Germany)', offset: '+01:00/+02:00' },
  { value: 'Australia/Sydney', label: 'AEST/AEDT (Sydney)', offset: '+10:00/+11:00' },
  { value: 'Asia/Dubai', label: 'GST (Gulf Standard Time)', offset: '+04:00' },
  { value: 'Asia/Singapore', label: 'SGT (Singapore)', offset: '+08:00' },
  { value: 'Asia/Tokyo', label: 'JST (Japan)', offset: '+09:00' },
  { value: 'America/Toronto', label: 'EST/EDT (Toronto)', offset: '-05:00/-04:00' },
  { value: 'Pacific/Auckland', label: 'NZST/NZDT (New Zealand)', offset: '+12:00/+13:00' },
];

/**
 * Get user's local timezone
 * @returns {string} IANA timezone string (e.g., 'America/New_York')
 */
export const getLocalTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch (e) {
    return 'Asia/Kolkata'; // Fallback to IST
  }
};

/**
 * Get a friendly display name for a timezone
 * @param {string} timezone - IANA timezone string
 * @returns {string} Friendly timezone name
 */
export const getTimezoneLabel = (timezone) => {
  const found = TIMEZONE_OPTIONS.find(tz => tz.value === timezone);
  if (found) return found.label;
  
  // Generate label from timezone string if not in our list
  try {
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'short'
    });
    const parts = formatter.formatToParts(new Date());
    const tzName = parts.find(p => p.type === 'timeZoneName')?.value || timezone;
    return `${tzName} (${timezone.split('/').pop().replace(/_/g, ' ')})`;
  } catch (e) {
    return timezone;
  }
};

/**
 * Convert a time from IST to another timezone
 * @param {string} date - Date string in YYYY-MM-DD format
 * @param {string} time - Time string in HH:MM format (IST)
 * @param {string} targetTimezone - Target IANA timezone
 * @returns {object} { date, time, dateChanged } - Converted date and time
 */
export const convertFromIST = (date, time, targetTimezone) => {
  if (!date || !time || targetTimezone === 'Asia/Kolkata') {
    return { date, time, dateChanged: false };
  }

  try {
    // Parse the IST datetime
    const [hours, minutes] = time.split(':').map(Number);
    const [year, month, day] = date.split('-').map(Number);
    
    // Create a date object representing the IST time
    // IST is UTC+5:30
    const istOffsetMinutes = 5 * 60 + 30;
    
    // Create UTC date from IST
    const utcDate = new Date(Date.UTC(year, month - 1, day, hours, minutes));
    utcDate.setMinutes(utcDate.getMinutes() - istOffsetMinutes);
    
    // Format in target timezone
    const formatter = new Intl.DateTimeFormat('en-CA', {
      timeZone: targetTimezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
    
    const parts = formatter.formatToParts(utcDate);
    const getPart = (type) => parts.find(p => p.type === type)?.value;
    
    const newDate = `${getPart('year')}-${getPart('month')}-${getPart('day')}`;
    const newTime = `${getPart('hour')}:${getPart('minute')}`;
    const dateChanged = newDate !== date;
    
    return { date: newDate, time: newTime, dateChanged };
  } catch (e) {
    console.error('Timezone conversion error:', e);
    return { date, time, dateChanged: false };
  }
};

/**
 * Convert a time from a source timezone to IST (for storing)
 * @param {string} date - Date string in YYYY-MM-DD format
 * @param {string} time - Time string in HH:MM format
 * @param {string} sourceTimezone - Source IANA timezone
 * @returns {object} { date, time } - Converted to IST
 */
export const convertToIST = (date, time, sourceTimezone) => {
  if (!date || !time || sourceTimezone === 'Asia/Kolkata') {
    return { date, time };
  }

  try {
    const [hours, minutes] = time.split(':').map(Number);
    const [year, month, day] = date.split('-').map(Number);
    
    // Create date in source timezone
    const sourceDate = new Date(
      new Date(year, month - 1, day, hours, minutes).toLocaleString('en-US', { timeZone: sourceTimezone })
    );
    
    // Get UTC timestamp
    const utcDate = new Date(Date.UTC(year, month - 1, day, hours, minutes));
    
    // Get offset for source timezone
    const sourceOffset = getTimezoneOffset(sourceTimezone, utcDate);
    utcDate.setMinutes(utcDate.getMinutes() - sourceOffset);
    
    // Convert to IST (UTC+5:30)
    const istOffsetMinutes = 5 * 60 + 30;
    utcDate.setMinutes(utcDate.getMinutes() + istOffsetMinutes);
    
    const newDate = utcDate.toISOString().split('T')[0];
    const newTime = `${String(utcDate.getUTCHours()).padStart(2, '0')}:${String(utcDate.getUTCMinutes()).padStart(2, '0')}`;
    
    return { date: newDate, time: newTime };
  } catch (e) {
    console.error('Timezone conversion error:', e);
    return { date, time };
  }
};

/**
 * Get timezone offset in minutes for a specific date
 * @param {string} timezone - IANA timezone
 * @param {Date} date - Date to check offset for
 * @returns {number} Offset in minutes
 */
const getTimezoneOffset = (timezone, date) => {
  const utcDate = new Date(date.toLocaleString('en-US', { timeZone: 'UTC' }));
  const tzDate = new Date(date.toLocaleString('en-US', { timeZone: timezone }));
  return (tzDate - utcDate) / 60000;
};

/**
 * Format time for display with AM/PM
 * @param {string} time - Time in HH:MM format
 * @returns {string} Formatted time (e.g., "2:30 PM")
 */
export const formatTimeDisplay = (time) => {
  if (!time) return '';
  const [hours, minutes] = time.split(':').map(Number);
  const period = hours >= 12 ? 'PM' : 'AM';
  const hour12 = hours % 12 || 12;
  return `${hour12}:${String(minutes).padStart(2, '0')} ${period}`;
};

/**
 * Get current timezone abbreviation
 * @param {string} timezone - IANA timezone
 * @returns {string} Abbreviation (e.g., "EST", "IST")
 */
export const getTimezoneAbbreviation = (timezone) => {
  try {
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'short'
    });
    const parts = formatter.formatToParts(new Date());
    return parts.find(p => p.type === 'timeZoneName')?.value || '';
  } catch (e) {
    return '';
  }
};
